import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useV2Api } from '../../../hooks/useV2Api'
import { friendlyErrorMessage } from '../../../utils/friendlyError'
import './V2Quotes.css'

function getScoreClass(score) {
  if (score >= 80) return 'v2-quote-score-circle--high'
  if (score >= 60) return 'v2-quote-score-circle--medium'
  return 'v2-quote-score-circle--low'
}

function getReliabilityColor(value) {
  if (value >= 90) return '#5C7A54'
  if (value >= 80) return '#A67B2C'
  return '#8B3A3A'
}

function formatMoney(n, currency = 'USD') {
  if (n == null) return '—'
  const num = typeof n === 'string' ? parseFloat(n) : n
  return num.toLocaleString('en-US', { style: 'currency', currency, maximumFractionDigits: 0 })
}

function reliabilityPct(score) {
  const n = typeof score === 'string' ? parseFloat(score) : score
  return Math.max(0, Math.min(100, Math.round((n || 0) * 100)))
}

const WEIGHT_COLORS = {
  costWeight: { label: 'Cost', color: '#8F432F' },
  speedWeight: { label: 'Speed', color: '#2B2560' },
  reliabilityWeight: { label: 'Reliability', color: '#5C7A54' }
}

export default function V2QuoteComparison() {
  const navigate = useNavigate()
  const { id: shipmentId } = useParams()
  const api = useV2Api()

  const [shipment, setShipment] = useState(null)
  const [comparison, setComparison] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [errorFallback, setErrorFallback] = useState(null)
  const [selecting, setSelecting] = useState(null)
  const [notification, setNotification] = useState(null) // { type: 'success' | 'warning', message: string }

  const load = useCallback(async () => {
    setLoading(true)
    setError(null); setErrorFallback(null)
    try {
      const [shipRes, cmpRes] = await Promise.all([
        api.request(`/api/v2/shipments/${shipmentId}`),
        api.request(`/api/v2/shipments/${shipmentId}/compare`, { method: 'POST' })
      ])
      setShipment(shipRes?.data || null)
      setComparison(cmpRes?.data || null)
    } catch (err) {
      setError(err); setErrorFallback('Failed to load comparison')
    } finally {
      setLoading(false)
    }
  }, [api, shipmentId])

  useEffect(() => { load() }, [load])

  const handleSelect = async (scoreEntry) => {
    if (!comparison?.comparison?.id) return
    const reason = window.prompt(
      `Select ${scoreEntry.providerName} for ${formatMoney(scoreEntry.totalCost)}? Briefly note why (audit trail):`,
      'Best balance of cost and transit time.'
    )
    if (!reason) return
    setSelecting(scoreEntry.quoteId)
    try {
      const res = await api.request(
        `/api/v2/shipments/${shipmentId}/quotes/${scoreEntry.quoteId}/select`,
        {
          method: 'POST',
          body: JSON.stringify({ comparisonId: comparison.comparison.id, reason })
        }
      )
      const data = res?.data || {}
      if (data.emailSent === false && data.emailError) {
        setNotification({
          type: 'warning',
          message: `Quote selected, but confirmation email failed: ${data.emailError}. Please contact the provider directly.`
        })
        setSelecting(null)
      } else {
        const providerName = data.selectedQuote?.providerName || scoreEntry.providerName || 'provider'
        setNotification({
          type: 'success',
          message: `Quote selected — confirmation email sent to ${providerName}.`
        })
        navigate(`/shipments/${shipmentId}`)
      }
    } catch (err) {
      // Pass the Error through so friendlyErrorMessage scrubs raw bodies.
      setError(err); setErrorFallback('Selection failed.')
      setSelecting(null)
    }
  }

  const route = useMemo(() => {
    if (!shipment) return ''
    if (shipment.quote_type === 'domestic_trucking') {
      const from = [shipment.pickup_city, shipment.pickup_state].filter(Boolean).join(', ')
      const to = (shipment.destination_address || '').slice(0, 60) || '—'
      return from ? `${from} → ${to}` : `Trucking → ${to}`
    }
    return `${shipment.origin_port || '—'} → ${shipment.destination_port || '—'}`
  }, [shipment])

  if (loading) return <div style={{ padding: '2rem', opacity: 0.6 }}>Loading comparison…</div>

  if (error && !comparison) {
    return (
      <div>
        <button className="v2-btn v2-btn--outline"
                onClick={() => navigate(`/shipments/${shipmentId}`)}
                style={{ marginBottom: 12 }}>← Back to Shipment</button>
        <div style={{
          padding: 16, borderRadius: 8, border: '1px solid #8B3A3A',
          background: 'rgba(139,58,58,0.08)', color: '#8B3A3A'
        }}>
          <strong>Couldn't build comparison.</strong>
          <div style={{ marginTop: 6, fontSize: 14 }}>{friendlyErrorMessage(error, errorFallback)}</div>
          <div style={{ marginTop: 6, fontSize: 13, opacity: 0.85 }}>
            Comparisons need at least one parsed quote with a total cost. If forwarders haven't replied with prices yet, wait for the next IMAP poll (≤ 5 min).
          </div>
        </div>
      </div>
    )
  }

  const scores = comparison?.scores || []
  const weights = comparison?.weights || { costWeight: 60, speedWeight: 30, reliabilityWeight: 10 }

  return (
    <div>
      <div className="v2-page-header">
        <div>
          <button className="v2-btn v2-btn--outline"
                  onClick={() => navigate(`/shipments/${shipmentId}`)}
                  style={{ marginBottom: 12, padding: '6px 14px', fontSize: 12 }}>
            ← Back to Shipment
          </button>
          <h1 className="v2-page-title">Compare quotes</h1>
          <p className="v2-page-subtitle">
            {route} · {scores.length} quote{scores.length === 1 ? '' : 's'} compared
          </p>
        </div>
      </div>

      {notification && (
        <div style={{
          padding: '10px 16px', marginBottom: 16, borderRadius: 8,
          fontSize: 14,
          background: notification.type === 'warning' ? 'rgba(166,123,44,0.1)' : 'rgba(92,122,84,0.1)',
          border: `1px solid ${notification.type === 'warning' ? '#A67B2C' : '#5C7A54'}`,
          color: notification.type === 'warning' ? '#A67B2C' : '#5C7A54'
        }}>
          {notification.message}
        </div>
      )}

      {comparison?.reasoning && (
        <div style={{
          padding: '12px 16px', marginBottom: 16, borderRadius: 8,
          background: '#F3F2ED', fontSize: 14, color: '#23201C'
        }}>
          <strong>Recommendation:</strong> {comparison.reasoning}
          {comparison?.costSavings > 0 && (
            <span style={{ marginLeft: 8, color: '#5C7A54' }}>
              · Cost spread: {formatMoney(comparison.costSavings)}
            </span>
          )}
        </div>
      )}

      <div className="v2-compare-legend" style={{ marginBottom: 24 }}>
        <span className="v2-compare-legend-title">
          Scoring weights ({comparison?.quoteType === 'domestic_trucking' ? 'trucking' : 'ocean'}):
        </span>
        {['costWeight', 'speedWeight', 'reliabilityWeight'].map((k) => (
          <div key={k} className="v2-compare-legend-item">
            <span className="v2-compare-legend-dot" style={{ background: WEIGHT_COLORS[k].color }} />
            <span>{WEIGHT_COLORS[k].label} {weights[k]}%</span>
          </div>
        ))}
      </div>

      {scores.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: '#8F8F85' }}>
          No comparable quotes yet. Quotes need a parsed total cost to be ranked.
        </div>
      ) : (
        <div className="v2-compare-grid">
          {scores.map((s) => (
            <div key={s.quoteId}
                 className={`v2-quote-card${s.recommended ? ' v2-quote-card--recommended' : ''}`}>
              {s.recommended && <span className="v2-quote-best-badge">Best Value</span>}

              <div className="v2-quote-card-header">
                <span className="v2-quote-provider-name">{s.providerName}</span>
              </div>

              <div className="v2-quote-score-wrapper">
                <div className={`v2-quote-score-circle ${getScoreClass(s.totalScore)}`}>
                  <span className="v2-quote-score-value">{Math.round(s.totalScore)}</span>
                  <span className="v2-quote-score-label">Score</span>
                </div>
              </div>

              <div className="v2-quote-total">
                <div className="v2-quote-total-amount">{formatMoney(s.totalCost)}</div>
                <div className="v2-quote-total-label">Total cost</div>
              </div>

              <div className="v2-quote-meta-row">
                <div className="v2-quote-meta-item">
                  <span className="v2-quote-meta-value">{s.transitDays || '—'} days</span>
                  <span className="v2-quote-meta-label">Transit</span>
                </div>
                <div className="v2-quote-meta-item">
                  <span className="v2-quote-meta-value">{reliabilityPct(s.providerReliability)}%</span>
                  <span className="v2-quote-meta-label">Reliability</span>
                  <div className="v2-quote-reliability-bar">
                    <div className="v2-quote-reliability-fill" style={{
                      width: `${reliabilityPct(s.providerReliability)}%`,
                      background: getReliabilityColor(reliabilityPct(s.providerReliability))
                    }} />
                  </div>
                </div>
              </div>

              <div style={{ fontSize: 11, color: '#8F8F85', marginTop: 8, textAlign: 'center' }}>
                Sub-scores · cost {Math.round(s.costScore)} · speed {Math.round(s.speedScore)} · reliability {Math.round(s.reliabilityScore)}
              </div>

              <div className="v2-quote-select-btn">
                <button className={`v2-btn ${s.recommended ? 'v2-btn--primary' : 'v2-btn--outline'}`}
                        style={{ width: '100%', justifyContent: 'center' }}
                        disabled={selecting === s.quoteId}
                        onClick={() => handleSelect(s)}>
                  {selecting === s.quoteId ? 'Selecting…' : (s.recommended ? 'Select best value' : 'Select this quote')}
                </button>
              </div>

              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <a href="#" onClick={(e) => {
                  e.preventDefault()
                  navigate(`/shipments/${shipmentId}/quotes/${s.quoteId}`)
                }} style={{ fontSize: 12, color: '#8F432F', textDecoration: 'none' }}>
                  View full breakdown →
                </a>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
